import toast from 'react-hot-toast';

// Styling options for different toast types
const toastOptions = {
  duration: 5000,
  style: {
    borderRadius: '10px',
    padding: '16px',
    fontWeight: '500',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
};

// Success toast with brand styling
export const showSuccess = (message, duration = 3000) => {
  return toast.success(message, {
    ...toastOptions,
    duration,
    style: {
      ...toastOptions.style,
      backgroundColor: '#FFFFFF',
      border: '1px solid var(--brand-teal)',
      color: 'var(--brand-teal-dark)',
    },
  });
};

// Error toast with brand styling
export const showError = (message, duration = 5000) => {
  return toast.error(message, {
    ...toastOptions,
    duration,
    style: {
      ...toastOptions.style,
      backgroundColor: '#FFFFFF',
      border: '1px solid #F87171',
      color: '#B91C1C',
    },
  });
};

// Info toast with brand styling
export const showInfo = (message, duration = 4000) => {
  return toast.success(message, {
    ...toastOptions,
    duration,
    icon: '📌',
    style: {
      ...toastOptions.style,
      backgroundColor: '#FFFFFF',
      border: '1px solid var(--brand-orange)',
      color: 'var(--brand-orange-dark)',
    },
  });
};

// Warning toast with brand styling
export const showWarning = (message, duration = 4000) => {
  return toast(message, {
    ...toastOptions,
    duration,
    icon: '⚠️',
    style: {
      ...toastOptions.style,
      backgroundColor: '#FFFFFF',
      border: '1px solid var(--brand-beige)',
      color: 'var(--brand-beige-dark)',
    },
  });
};

// Handle API responses and show appropriate toast notifications
export const handleResponse = (response, { 
  successMessage, 
  errorMessage = 'Operation failed', 
  duration,
  onSuccess = () => {} 
}) => {
  try {
    // If response is an error object
    if (response instanceof Error) {
      throw response;
    }

    // Check for success based on response structure or status code
    // The backend returns 200 for successful operations
    const isSuccess = 
      response?.success || // Bulk operations return success flag
      response?.id ||      // Single operations return updated entity
      (response && typeof response === 'object' && Object.keys(response).length > 0); // Any non-empty object is likely success
    
    if (isSuccess) {
      showSuccess(successMessage, duration);
      try {
        onSuccess(response);
      } catch (callbackError) {
        console.error('Error in success callback:', callbackError);
      }
    } else {
      // Get error message from various possible locations in the response
      const message = response?.message || 
                     response?.error ||
                     errorMessage;
      throw new Error(message);
    }
  } catch (error) {
    // Handle different error types
    const finalErrorMessage = error.status === 403 
      ? 'You do not have permission to perform this action'
      : error.status === 404
      ? 'Resource not found'
      : error.message || errorMessage;

    showError(finalErrorMessage, duration);
    
    // Log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Response error:', error);
    }
  }
};

// For backward compatibility with the old responseSlice
export const showResponse = ({ message, severity = 'info', duration = 5000 }) => {
  switch (severity) {
    case 'success':
      return showSuccess(message, duration);
    case 'error':
      return showError(message, duration);
    case 'warning':
      return showWarning(message, duration);
    case 'info':
    default:
      return showInfo(message, duration);
  }
};

// This is a no-op since toast automatically handles hiding
export const hideResponse = () => {}; 